import { ReactNode, Children, isValidElement, cloneElement } from 'react'
import { clsx } from 'clsx'

export type MarqueeSpeed = 'slow' | 'normal' | 'fast'

interface MarqueeRowProps {
  /** Items to scroll. Will be duplicated internally for seamless loop. */
  children: ReactNode
  speed?: MarqueeSpeed
  /** Reverse scroll direction (right to left → left to right) */
  reverse?: boolean
  /** Pause animation when hovering anywhere over the marquee */
  pauseOnHover?: boolean
  /** Gap between items in pixels (default 16) */
  gap?: number
  className?: string
}

const DURATION_MAP: Record<MarqueeSpeed, string> = {
  slow: '60s',
  normal: '40s',
  fast: '25s',
}

export function MarqueeRow({
  children,
  speed = 'normal',
  reverse = false,
  pauseOnHover = false,
  gap = 16,
  className,
}: MarqueeRowProps) {
  const items = Children.toArray(children)

  // Duplicate items so the second half visually replaces the first as it scrolls.
  const renderItems = (keyPrefix: string) =>
    items.map((child, idx) =>
      isValidElement(child)
        ? cloneElement(child, { key: `${keyPrefix}-${idx}` })
        : child
    )

  return (
    <div className={clsx('w-full overflow-hidden', className)}>
      <div
        className={clsx(
          'flex w-max',
          pauseOnHover && 'hover:[animation-play-state:paused]'
        )}
        style={{
          gap: `${gap}px`,
          animationName: 'qb-marquee',
          animationTimingFunction: 'linear',
          animationIterationCount: 'infinite',
          animationDuration: DURATION_MAP[speed],
          animationDirection: reverse ? 'reverse' : 'normal',
        }}
      >
        <div className="flex shrink-0" style={{ gap: `${gap}px` }}>
          {renderItems('a')}
        </div>
        <div
          aria-hidden
          className="flex shrink-0"
          style={{ gap: `${gap}px` }}
        >
          {renderItems('b')}
        </div>
      </div>
    </div>
  )
}
